// @ts-nocheck
// ============================================================
// BIKE GEOMETRY DATABASE
// Fields per model:
//   category: Road | Gravel | CX | MTB | TT
//   modelName: display name
//   years: year range string
//   cockpitType: standard | integrated | semi-integrated
//   handlebarType: dropbar | flatbar | aerobar
//   wheelSize: "700c" | "650b" | "29" | "27.5" | "mixed"
//   suspensionTravel: null for road/gravel, { front: mm, rear: mm } for MTB
//   seatTubeAngle: default STA (can be overridden per size)
//   seatpostDiameter: mm (e.g. 27.2, 31.6, custom)
//   sizeType: numeric_cm | numeric_mm | alpha
//   sizes: { sizeName: { stack, reach, seatTubeAngle?, headTubeAngle?, bbDrop?, chainstayLength?, wheelbase? } }
//
// GEOMETRY COMPLETENESS KEY:
//   ✅ = stack/reach/STA + headTubeAngle/bbDrop/chainstayLength/wheelbase all verified from manufacturer
//   🔶 = stack/reach/STA verified from manufacturer; extended geo pending
//   ❌ = data not yet verified against manufacturer source
//
// CORRECTIONS LOG:
//   [C1] Cervelo R5 (2020-2025): All stack/reach values corrected from Cervelo support page.
//   [C2] Canyon Aeroad CFR (2024): STA corrected to constant 73.5.
//   [C3] Canyon Ultimate CF SL (2024): STA corrected to 73.8 constant; 3XS and 2XS corrected.
//   [C4] Canyon Endurace CF SLX (2024): XS stack corrected; 2XS added; STA constant 73.5.
//   [C5] Canyon Grail CFR (2024): STA corrected to constant 73.5.
//   [C6] Pinarello Dogma F (both years): STA values were completely wrong (~83-84°, likely copied
//        from a proprietary virtual STA measurement, not the actual seat tube angle).
//        Corrected to real frame STAs sourced from veloholiccycles.com geometry table.
//        Stack/reach values were already correct and unchanged.
// ============================================================

export const BIKES = {
  // ── CERVELO ──────────────────────────────────────────────
  Cervelo: {
    models: {
      "S5 (2020-2024)": {
        // ✅ stack/reach/STA + extended geo verified from cervelo.com/en-US/support/S5 DISC MK3
        category: "Road",
        modelName: "S5",
        years: "2020-2024",
        cockpitType: "integrated",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73,
        seatpostDiameter: "integrated",
        sizeType: "numeric_cm",
        sizes: {
          48: {
            stack: 496,
            reach: 367,
            headTubeAngle: 71.0,
            bbDrop: 72.5,
            chainstayLength: 405,
            wheelbase: 973,
          },
          51: {
            stack: 519,
            reach: 376,
            headTubeAngle: 72.0,
            bbDrop: 72.5,
            chainstayLength: 405,
            wheelbase: 974,
          },
          54: {
            stack: 542,
            reach: 384,
            headTubeAngle: 73.0,
            bbDrop: 70.0,
            chainstayLength: 405,
            wheelbase: 975,
          },
          56: {
            stack: 565,
            reach: 392,
            headTubeAngle: 73.5,
            bbDrop: 70.0,
            chainstayLength: 405,
            wheelbase: 982,
          },
          58: {
            stack: 588,
            reach: 401,
            headTubeAngle: 73.5,
            bbDrop: 68.0,
            chainstayLength: 405,
            wheelbase: 999,
          },
        },
      },
      "S5 (2025)": {
        // 🔶 stack/reach verified; extended geo pending
        category: "Road",
        modelName: "S5",
        years: "2025",
        cockpitType: "integrated",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73,
        seatpostDiameter: "integrated",
        sizeType: "numeric_cm",
        sizes: {
          48: { stack: 496, reach: 367 },
          51: { stack: 519, reach: 376 },
          54: { stack: 542, reach: 384 },
          56: { stack: 565, reach: 392 },
          58: { stack: 588, reach: 401 },
          61: { stack: 608, reach: 409 },
        },
      },
      "S3 (2016-2020)": {
        // ❌ stack/reach not yet verified against manufacturer source
        category: "Road",
        modelName: "S3",
        years: "2016-2020",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73,
        seatpostDiameter: 27.2,
        sizeType: "numeric_cm",
        sizes: {
          48: { stack: 505, reach: 360 },
          51: { stack: 530, reach: 369 },
          54: { stack: 555, reach: 378 },
          56: { stack: 580, reach: 386 },
          58: { stack: 605, reach: 396 },
          61: { stack: 630, reach: 405 },
        },
      },
      "Soloist (2023-2025)": {
        // ❌ stack/reach not yet verified against manufacturer source
        category: "Road",
        modelName: "Soloist",
        years: "2023-2025",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73,
        seatpostDiameter: 27.2,
        sizeType: "numeric_cm",
        sizes: {
          48: { stack: 505, reach: 360 },
          51: { stack: 530, reach: 369 },
          54: { stack: 555, reach: 378 },
          56: { stack: 580, reach: 387 },
          58: { stack: 605, reach: 396 },
          61: { stack: 630, reach: 405 },
        },
      },
      "R5 (2020-2025)": {
        // ✅ All values verified from cervelo.com/en-US/support/R5 DISC MK4
        // [C1] Stack/reach corrected across all sizes vs previous version
        category: "Road",
        modelName: "R5",
        years: "2020-2025",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73,
        seatpostDiameter: 27.2,
        sizeType: "numeric_cm",
        sizes: {
          48: {
            stack: 497,
            reach: 363,
            headTubeAngle: 71.0,
            bbDrop: 74.5,
            chainstayLength: 410,
            wheelbase: 972,
          },
          51: {
            stack: 522,
            reach: 371,
            headTubeAngle: 72.0,
            bbDrop: 74.5,
            chainstayLength: 410,
            wheelbase: 974,
          },
          54: {
            stack: 547,
            reach: 380,
            headTubeAngle: 73.0,
            bbDrop: 72.0,
            chainstayLength: 410,
            wheelbase: 977,
          },
          56: {
            stack: 572,
            reach: 389,
            headTubeAngle: 73.0,
            bbDrop: 72.0,
            chainstayLength: 410,
            wheelbase: 994,
          },
          58: {
            stack: 597,
            reach: 398,
            headTubeAngle: 73.0,
            bbDrop: 69.5,
            chainstayLength: 410,
            wheelbase: 1011,
          },
          61: {
            stack: 622,
            reach: 407,
            headTubeAngle: 73.0,
            bbDrop: 69.5,
            chainstayLength: 410,
            wheelbase: 1028,
          },
        },
      },
      "Aspero (2022-2024)": {
        // ❌ stack/reach not yet verified against manufacturer source
        category: "Gravel",
        modelName: "Aspero",
        years: "2022-2024",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 27.2,
        sizeType: "numeric_cm",
        sizes: {
          48: { stack: 505, reach: 370 },
          51: { stack: 530, reach: 379 },
          54: { stack: 555, reach: 388 },
          56: { stack: 580, reach: 397 },
          58: { stack: 605, reach: 406 },
          61: { stack: 630, reach: 415 },
        },
      },
    },
  },

  // ── PINARELLO ────────────────────────────────────────────
  Pinarello: {
    models: {
      "Dogma F (2022-2023)": {
        // ✅ Stack/reach verified. STA corrected [C6] — previous values (83-84°) were wrong.
        // Real frame STAs sourced from veloholiccycles.com geometry table.
        // Stack/reach values unchanged (were already correct).
        // Source: veloholiccycles.com/pages/pinarello-dogma-f
        category: "Road",
        modelName: "Dogma F",
        years: "2022-2023",
        cockpitType: "integrated",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.4,
        seatpostDiameter: "integrated",
        sizeType: "numeric_mm",
        sizes: {
          425: { stack: 502, reach: 351, seatTubeAngle: 74.4 },
          450: { stack: 518, reach: 365, seatTubeAngle: 74.4 },
          465: { stack: 525, reach: 372, seatTubeAngle: 74.0 },
          480: { stack: 532, reach: 378, seatTubeAngle: 73.7 },
          495: { stack: 543, reach: 383, seatTubeAngle: 73.7 },
          515: { stack: 551, reach: 385, seatTubeAngle: 73.4 },
          530: { stack: 562, reach: 389, seatTubeAngle: 73.4 },
          550: { stack: 570, reach: 391, seatTubeAngle: 73.0 },
          565: { stack: 585, reach: 397, seatTubeAngle: 73.0 },
          600: { stack: 613, reach: 393, seatTubeAngle: 72.4 },
          620: { stack: 652, reach: 410, seatTubeAngle: 72.0 },
        },
      },
      "Dogma F (2024)": {
        // ✅ Stack/reach verified. STA corrected [C6] — same correction as 2022-2023.
        // Geometry is near-identical to 2022-2023 with minor stack/reach revisions on larger sizes.
        // Source: veloholiccycles.com/pages/pinarello-dogma-f
        category: "Road",
        modelName: "Dogma F",
        years: "2024",
        cockpitType: "integrated",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.4,
        seatpostDiameter: "integrated",
        sizeType: "numeric_mm",
        sizes: {
          425: { stack: 502, reach: 352, seatTubeAngle: 74.4 },
          450: { stack: 517, reach: 365, seatTubeAngle: 74.4 },
          465: { stack: 525, reach: 372, seatTubeAngle: 74.0 },
          480: { stack: 532, reach: 378, seatTubeAngle: 73.7 },
          495: { stack: 542, reach: 383, seatTubeAngle: 73.7 },
          515: { stack: 551, reach: 385, seatTubeAngle: 73.4 },
          530: { stack: 562, reach: 389, seatTubeAngle: 73.4 },
          550: { stack: 570, reach: 391, seatTubeAngle: 73.0 },
          565: { stack: 585, reach: 397, seatTubeAngle: 73.0 },
          600: { stack: 599, reach: 395, seatTubeAngle: 72.4 },
          620: { stack: 634, reach: 400, seatTubeAngle: 72.0 },
        },
      },
    },
  },

  // ── CANYON ───────────────────────────────────────────────
  Canyon: {
    models: {
      "Aeroad CFR (2024)": {
        // ✅ All values verified from canyon.com productpdf geometry
        // [C2] STA corrected to constant 73.5
        category: "Road",
        modelName: "Aeroad CFR",
        years: "2024",
        cockpitType: "integrated",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: "integrated",
        sizeType: "alpha",
        sizes: {
          "2XS": {
            stack: 498,
            reach: 372,
            seatTubeAngle: 73.5,
            headTubeAngle: 70.5,
            bbDrop: 72,
            chainstayLength: 410,
            wheelbase: 971,
          },
          XS: {
            stack: 521,
            reach: 378,
            seatTubeAngle: 73.5,
            headTubeAngle: 71.5,
            bbDrop: 72,
            chainstayLength: 410,
            wheelbase: 976,
          },
          S: {
            stack: 539,
            reach: 390,
            seatTubeAngle: 73.5,
            headTubeAngle: 72.75,
            bbDrop: 70,
            chainstayLength: 410,
            wheelbase: 983,
          },
          M: {
            stack: 560,
            reach: 393,
            seatTubeAngle: 73.5,
            headTubeAngle: 73.25,
            bbDrop: 70,
            chainstayLength: 410,
            wheelbase: 988,
          },
          L: {
            stack: 580,
            reach: 401,
            seatTubeAngle: 73.5,
            headTubeAngle: 73.25,
            bbDrop: 70,
            chainstayLength: 410,
            wheelbase: 1001,
          },
          XL: {
            stack: 606,
            reach: 419,
            seatTubeAngle: 73.5,
            headTubeAngle: 73.5,
            bbDrop: 70,
            chainstayLength: 410,
            wheelbase: 1025,
          },
          "2XL": {
            stack: 624,
            reach: 429,
            seatTubeAngle: 73.5,
            headTubeAngle: 73.75,
            bbDrop: 70,
            chainstayLength: 410,
            wheelbase: 1037,
          },
        },
      },
      "Ultimate CF SL (2024)": {
        // ✅ All values verified from canyon.com productpdf geometry
        // [C3] STA corrected to 73.8 constant; 3XS and 2XS corrected
        category: "Road",
        modelName: "Ultimate CF SL",
        years: "2024",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.8,
        seatpostDiameter: 27.2,
        sizeType: "alpha",
        sizes: {
          "3XS": {
            stack: 500,
            reach: 368,
            seatTubeAngle: 73.9,
            headTubeAngle: 69.6,
            bbDrop: 72,
            chainstayLength: 415,
            wheelbase: 983,
          },
          "2XS": {
            stack: 522,
            reach: 378,
            seatTubeAngle: 73.8,
            headTubeAngle: 71.0,
            bbDrop: 72,
            chainstayLength: 415,
            wheelbase: 988,
          },
          XS: {
            stack: 546,
            reach: 385,
            seatTubeAngle: 73.8,
            headTubeAngle: 72.25,
            bbDrop: 70,
            chainstayLength: 415,
            wheelbase: 993,
          },
          S: {
            stack: 567,
            reach: 391,
            seatTubeAngle: 73.8,
            headTubeAngle: 73.25,
            bbDrop: 70,
            chainstayLength: 415,
            wheelbase: 996,
          },
          M: {
            stack: 592,
            reach: 399,
            seatTubeAngle: 73.8,
            headTubeAngle: 73.25,
            bbDrop: 70,
            chainstayLength: 415,
            wheelbase: 1011,
          },
          L: {
            stack: 613,
            reach: 418,
            seatTubeAngle: 73.8,
            headTubeAngle: 73.5,
            bbDrop: 70,
            chainstayLength: 415,
            wheelbase: 1034,
          },
          "2XL": {
            stack: 631,
            reach: 428,
            seatTubeAngle: 73.8,
            headTubeAngle: 73.75,
            bbDrop: 70,
            chainstayLength: 415,
            wheelbase: 1046,
          },
        },
      },
      "Endurace CF SLX (2024)": {
        // ✅ All values verified from canyon.com productpdf geometry
        // [C4] XS stack corrected; 2XS added; STA constant 73.5
        category: "Road",
        modelName: "Endurace CF SLX",
        years: "2024",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 27.2,
        sizeType: "alpha",
        sizes: {
          "2XS": {
            stack: 510,
            reach: 360,
            seatTubeAngle: 73.5,
            headTubeAngle: 69.2,
            bbDrop: 75,
            chainstayLength: 415,
            wheelbase: 980,
          },
          XS: {
            stack: 532,
            reach: 370,
            seatTubeAngle: 73.5,
            headTubeAngle: 71.0,
            bbDrop: 75,
            chainstayLength: 415,
            wheelbase: 982,
          },
          S: {
            stack: 557,
            reach: 376,
            seatTubeAngle: 73.5,
            headTubeAngle: 72.0,
            bbDrop: 73,
            chainstayLength: 415,
            wheelbase: 988,
          },
          M: {
            stack: 578,
            reach: 382,
            seatTubeAngle: 73.5,
            headTubeAngle: 73.0,
            bbDrop: 73,
            chainstayLength: 415,
            wheelbase: 990,
          },
          L: {
            stack: 604,
            reach: 389,
            seatTubeAngle: 73.5,
            headTubeAngle: 73.0,
            bbDrop: 73,
            chainstayLength: 415,
            wheelbase: 1006,
          },
          XL: {
            stack: 625,
            reach: 407,
            seatTubeAngle: 73.5,
            headTubeAngle: 73.25,
            bbDrop: 73,
            chainstayLength: 415,
            wheelbase: 1028,
          },
          "2XL": {
            stack: 644,
            reach: 417,
            seatTubeAngle: 73.5,
            headTubeAngle: 73.25,
            bbDrop: 73,
            chainstayLength: 415,
            wheelbase: 1043,
          },
        },
      },
      "Grail CFR (2024)": {
        // ✅ All values verified from canyon.com productpdf geometry
        // [C5] STA corrected to constant 73.5
        category: "Gravel",
        modelName: "Grail CFR",
        years: "2024",
        cockpitType: "integrated",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: "integrated",
        sizeType: "alpha",
        sizes: {
          XS: {
            stack: 545,
            reach: 372,
            seatTubeAngle: 73.5,
            headTubeAngle: 71.0,
            bbDrop: 75,
            chainstayLength: 425,
            wheelbase: 1024,
          },
          S: {
            stack: 556,
            reach: 385,
            seatTubeAngle: 73.5,
            headTubeAngle: 71.5,
            bbDrop: 75,
            chainstayLength: 425,
            wheelbase: 1034,
          },
          M: {
            stack: 573,
            reach: 394,
            seatTubeAngle: 73.5,
            headTubeAngle: 71.5,
            bbDrop: 75,
            chainstayLength: 425,
            wheelbase: 1057,
          },
          L: {
            stack: 591,
            reach: 411,
            seatTubeAngle: 73.5,
            headTubeAngle: 71.5,
            bbDrop: 75,
            chainstayLength: 425,
            wheelbase: 1080,
          },
          XL: {
            stack: 613,
            reach: 427,
            seatTubeAngle: 73.5,
            headTubeAngle: 71.8,
            bbDrop: 75,
            chainstayLength: 425,
            wheelbase: 1092,
          },
          "2XL": {
            stack: 633,
            reach: 435,
            seatTubeAngle: 73.5,
            headTubeAngle: 71.8,
            bbDrop: 75,
            chainstayLength: 425,
            wheelbase: 1118,
          },
          "3XL": {
            stack: 655,
            reach: 454,
            seatTubeAngle: 73.5,
            headTubeAngle: 71.8,
            bbDrop: 75,
            chainstayLength: 425,
            wheelbase: 1140,
          },
        },
      },
    },
  },

  // ── GIANT ────────────────────────────────────────────────
  Giant: {
    models: {
      "TCR Advanced SL (2024)": {
        // ❌ stack/reach not yet re-verified against manufacturer source
        category: "Road",
        modelName: "TCR Advanced SL",
        years: "2024",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.0,
        seatpostDiameter: 27.2,
        sizeType: "alpha",
        sizes: {
          XS: { stack: 517, reach: 376, seatTubeAngle: 74.0 },
          S: { stack: 528, reach: 383, seatTubeAngle: 73.5 },
          M: { stack: 545, reach: 388, seatTubeAngle: 73.0 },
          ML: { stack: 562, reach: 393, seatTubeAngle: 73.0 },
          L: { stack: 581, reach: 402, seatTubeAngle: 72.5 },
          XL: { stack: 596, reach: 412, seatTubeAngle: 72.5 },
        },
      },
      "Defy Advanced SL (2024)": {
        // ❌ stack/reach not yet re-verified against manufacturer source
        category: "Road",
        modelName: "Defy Advanced SL",
        years: "2024",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.0,
        seatpostDiameter: 27.2,
        sizeType: "alpha",
        sizes: {
          XS: { stack: 527, reach: 369, seatTubeAngle: 74.0 },
          S: { stack: 541, reach: 375, seatTubeAngle: 73.5 },
          M: { stack: 558, reach: 380, seatTubeAngle: 73.0 },
          ML: { stack: 577, reach: 384, seatTubeAngle: 73.0 },
          L: { stack: 596, reach: 393, seatTubeAngle: 72.5 },
          XL: { stack: 615, reach: 402, seatTubeAngle: 72.5 },
        },
      },
      "Propel Advanced SL (2023-2025)": {
        // ✅ stack/reach/STA/HTA/bbDrop/chainstay/wheelbase all verified from giant-bicycles.com geometry chart
        category: "Road",
        modelName: "Propel Advanced SL",
        years: "2023-2025",
        cockpitType: "integrated",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73,
        seatpostDiameter: 27.2,
        sizeType: "alpha",
        sizes: {
          XS: {
            stack: 517,
            reach: 376,
            seatTubeAngle: 74.5,
            headTubeAngle: 71,
            bbDrop: 72,
            chainstayLength: 405,
            wheelbase: 976,
          },
          S: {
            stack: 528,
            reach: 383,
            seatTubeAngle: 74,
            headTubeAngle: 72.3,
            bbDrop: 70,
            chainstayLength: 405,
            wheelbase: 977,
          },
          M: {
            stack: 545,
            reach: 388,
            seatTubeAngle: 73.5,
            headTubeAngle: 73,
            bbDrop: 70,
            chainstayLength: 405,
            wheelbase: 980,
          },
          ML: {
            stack: 562,
            reach: 393,
            seatTubeAngle: 73,
            headTubeAngle: 73,
            bbDrop: 67,
            chainstayLength: 405,
            wheelbase: 991,
          },
          L: {
            stack: 581,
            reach: 402,
            seatTubeAngle: 73,
            headTubeAngle: 73,
            bbDrop: 67,
            chainstayLength: 405,
            wheelbase: 1006,
          },
          XL: {
            stack: 596,
            reach: 412,
            seatTubeAngle: 72.5,
            headTubeAngle: 73,
            bbDrop: 67,
            chainstayLength: 405,
            wheelbase: 1020,
          },
        },
      },
    },
  },

  // ── TREK ─────────────────────────────────────────────────
  Trek: {
    models: {
      "Madone SLR Gen 8 (2024)": {
        // ❌ stack/reach not yet re-verified against manufacturer source
        category: "Road",
        modelName: "Madone SLR Gen 8",
        years: "2024",
        cockpitType: "integrated",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.0,
        seatpostDiameter: "integrated",
        sizeType: "alpha",
        sizes: {
          XS: { stack: 507, reach: 370, seatTubeAngle: 74.6 },
          S: { stack: 530, reach: 378, seatTubeAngle: 74.2 },
          M: { stack: 546, reach: 384, seatTubeAngle: 73.7 },
          ML: { stack: 562, reach: 389, seatTubeAngle: 73.3 },
          L: { stack: 582, reach: 394, seatTubeAngle: 73.0 },
          XL: { stack: 610, reach: 402, seatTubeAngle: 72.5 },
        },
      },
      "Madone SLR Gen 7 (2023)": {
        // ❌ stack/reach not yet re-verified against manufacturer source
        category: "Road",
        modelName: "Madone SLR Gen 7",
        years: "2023",
        cockpitType: "integrated",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.0,
        seatpostDiameter: "integrated",
        sizeType: "numeric_cm",
        sizes: {
          "50cm": { stack: 521, reach: 378, seatTubeAngle: 74.6 },
          "52cm": { stack: 533, reach: 383, seatTubeAngle: 74.2 },
          "54cm": { stack: 541, reach: 386, seatTubeAngle: 73.7 },
          "56cm": { stack: 563, reach: 391, seatTubeAngle: 73.3 },
          "58cm": { stack: 581, reach: 396, seatTubeAngle: 73.0 },
          "60cm": { stack: 601, reach: 399, seatTubeAngle: 72.8 },
          "62cm": { stack: 620, reach: 403, seatTubeAngle: 72.5 },
        },
      },
      "Emonda SLR (2021-2024)": {
        // ✅ All values verified from bikeinsights (sourced from Trek manufacturer data)
        category: "Road",
        modelName: "Emonda SLR",
        years: "2021-2024",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.3,
        seatpostDiameter: 31.6,
        sizeType: "numeric_cm",
        sizes: {
          "47cm": {
            stack: 507,
            reach: 373,
            seatTubeAngle: 74.6,
            headTubeAngle: 72.1,
            bbDrop: 72,
            chainstayLength: 410,
            wheelbase: 972,
          },
          "50cm": {
            stack: 521,
            reach: 378,
            seatTubeAngle: 74.6,
            headTubeAngle: 72.1,
            bbDrop: 72,
            chainstayLength: 410,
            wheelbase: 974,
          },
          "52cm": {
            stack: 533,
            reach: 383,
            seatTubeAngle: 74.2,
            headTubeAngle: 72.8,
            bbDrop: 72,
            chainstayLength: 410,
            wheelbase: 977,
          },
          "54cm": {
            stack: 541,
            reach: 386,
            seatTubeAngle: 73.7,
            headTubeAngle: 73.0,
            bbDrop: 70,
            chainstayLength: 410,
            wheelbase: 981,
          },
          "56cm": {
            stack: 563,
            reach: 391,
            seatTubeAngle: 73.3,
            headTubeAngle: 73.5,
            bbDrop: 70,
            chainstayLength: 410,
            wheelbase: 983,
          },
          "58cm": {
            stack: 581,
            reach: 396,
            seatTubeAngle: 73.0,
            headTubeAngle: 73.8,
            bbDrop: 68,
            chainstayLength: 411,
            wheelbase: 992,
          },
          "60cm": {
            stack: 601,
            reach: 399,
            seatTubeAngle: 72.8,
            headTubeAngle: 73.9,
            bbDrop: 68,
            chainstayLength: 411,
            wheelbase: 1001,
          },
          "62cm": {
            stack: 620,
            reach: 403,
            seatTubeAngle: 72.5,
            headTubeAngle: 73.9,
            bbDrop: 68,
            chainstayLength: 412,
            wheelbase: 1010,
          },
        },
      },
      "Checkpoint SL (2023-2024)": {
        // ❌ stack/reach not yet re-verified against manufacturer source
        category: "Gravel",
        modelName: "Checkpoint SL",
        years: "2023-2024",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 31.6,
        sizeType: "numeric_cm",
        sizes: {
          "49cm": { stack: 538, reach: 393, seatTubeAngle: 74.5 },
          "52cm": { stack: 553, reach: 399, seatTubeAngle: 74.0 },
          "54cm": { stack: 571, reach: 403, seatTubeAngle: 73.5 },
          "56cm": { stack: 592, reach: 407, seatTubeAngle: 73.5 },
          "58cm": { stack: 609, reach: 411, seatTubeAngle: 73.0 },
          "62cm": { stack: 639, reach: 417, seatTubeAngle: 73.0 },
        },
      },
    },
  },

  // ── TIME ─────────────────────────────────────────────────
  TIME: {
    models: {
      "ADHX (2023-2024)": {
        // ❌ stack/reach not yet re-verified against manufacturer source
        category: "Road",
        modelName: "ADHX",
        years: "2023-2024",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.0,
        seatpostDiameter: 27.2,
        sizeType: "alpha",
        sizes: {
          XS: { stack: 530, reach: 366, seatTubeAngle: 73.5 },
          S: { stack: 543, reach: 373 },
          M: { stack: 562, reach: 383 },
          L: { stack: 581, reach: 392 },
          XL: { stack: 601, reach: 397 },
        },
      },
      "ADHX 45 (2023-2024)": {
        // ❌ stack/reach not yet re-verified against manufacturer source
        category: "Road",
        modelName: "ADHX 45",
        years: "2023-2024",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.0,
        seatpostDiameter: 27.2,
        sizeType: "alpha",
        sizes: {
          XS: { stack: 532, reach: 364, seatTubeAngle: 73.7 },
          S: { stack: 543, reach: 371 },
          M: { stack: 564, reach: 381 },
          L: { stack: 583, reach: 390 },
          XL: { stack: 603, reach: 395 },
        },
      },
    },
  },

  // ── SPECIALIZED ──────────────────────────────────────────
  Specialized: {
    models: {
      "Tarmac SL8 (2023-2025)": {
        // ❌ stack/reach not yet re-verified against manufacturer source
        category: "Road",
        modelName: "Tarmac SL8",
        years: "2023-2025",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 20.0,
        sizeType: "numeric_cm",
        sizes: {
          "44cm": { stack: 501, reach: 366, seatTubeAngle: 76.0 },
          "49cm": { stack: 514, reach: 375, seatTubeAngle: 75.0 },
          "52cm": { stack: 527, reach: 380, seatTubeAngle: 74.5 },
          "54cm": { stack: 544, reach: 384, seatTubeAngle: 74.0 },
          "56cm": { stack: 565, reach: 395, seatTubeAngle: 73.5 },
          "58cm": { stack: 591, reach: 402, seatTubeAngle: 73.0 },
          "61cm": { stack: 612, reach: 408, seatTubeAngle: 72.5 },
        },
      },
    },
  },

  // ── CANNONDALE ───────────────────────────────────────────
  Cannondale: {
    models: {
      "SuperSix EVO (2023-2024)": {
        // ❌ stack/reach not yet re-verified against manufacturer source
        category: "Road",
        modelName: "SuperSix EVO",
        years: "2023-2024",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.3,
        seatpostDiameter: 27.2,
        sizeType: "numeric_cm",
        sizes: {
          "44cm": { stack: 505, reach: 370, seatTubeAngle: 74.3 },
          "48cm": { stack: 520, reach: 374, seatTubeAngle: 74.3 },
          "51cm": { stack: 535, reach: 378, seatTubeAngle: 73.7 },
          "54cm": { stack: 555, reach: 384, seatTubeAngle: 73.3 },
          "56cm": { stack: 575, reach: 389, seatTubeAngle: 72.9 },
          "58cm": { stack: 595, reach: 395, seatTubeAngle: 72.5 },
          "61cm": { stack: 625, reach: 403, seatTubeAngle: 72.1 },
        },
      },
      "Synapse Carbon (2022-2024)": {
        // ❌ stack/reach not yet re-verified against manufacturer source
        category: "Road",
        modelName: "Synapse Carbon",
        years: "2022-2024",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 27.2,
        sizeType: "numeric_cm",
        sizes: {
          "44cm": { stack: 510, reach: 365, seatTubeAngle: 74.5 },
          "48cm": { stack: 530, reach: 371, seatTubeAngle: 74.0 },
          "51cm": { stack: 550, reach: 376, seatTubeAngle: 73.5 },
          "54cm": { stack: 570, reach: 381, seatTubeAngle: 73.5 },
          "56cm": { stack: 590, reach: 387, seatTubeAngle: 73.0 },
          "58cm": { stack: 610, reach: 393, seatTubeAngle: 73.0 },
          "61cm": { stack: 640, reach: 402, seatTubeAngle: 72.5 },
        },
      },
      "Topstone Carbon (2022-2024)": {
        // ❌ stack/reach not yet re-verified against manufacturer source
        category: "Gravel",
        modelName: "Topstone Carbon",
        years: "2022-2024",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 72.4,
        seatpostDiameter: 27.2,
        sizeType: "alpha",
        sizes: {
          XS: { stack: 534, reach: 371, seatTubeAngle: 73.0 },
          S: { stack: 551, reach: 377, seatTubeAngle: 72.4 },
          M: { stack: 574, reach: 383, seatTubeAngle: 72.4 },
          L: { stack: 600, reach: 390, seatTubeAngle: 72.4 },
          XL: { stack: 624, reach: 397, seatTubeAngle: 72.4 },
        },
      },
      "CAAD12 (2016-2020)": {
        // ✅ All values verified from bikeinsights (sourced from Cannondale manufacturer data)
        category: "Road",
        modelName: "CAAD12",
        years: "2016-2020",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 27.2,
        sizeType: "numeric_cm",
        sizes: {
          "44cm": {
            stack: 514,
            reach: 362,
            seatTubeAngle: 75.0,
            headTubeAngle: 70.5,
            bbDrop: 74,
            chainstayLength: 405,
          },
          "48cm": {
            stack: 516,
            reach: 369,
            seatTubeAngle: 74.7,
            headTubeAngle: 71.5,
            bbDrop: 74,
            chainstayLength: 405,
          },
          "50cm": {
            stack: 526,
            reach: 375,
            seatTubeAngle: 74.4,
            headTubeAngle: 72.0,
            bbDrop: 74,
            chainstayLength: 405,
          },
          "52cm": {
            stack: 536,
            reach: 381,
            seatTubeAngle: 74.1,
            headTubeAngle: 72.6,
            bbDrop: 72,
            chainstayLength: 405,
          },
          "54cm": {
            stack: 551,
            reach: 387,
            seatTubeAngle: 73.8,
            headTubeAngle: 72.9,
            bbDrop: 72,
            chainstayLength: 405,
          },
          "56cm": {
            stack: 567,
            reach: 393,
            seatTubeAngle: 73.5,
            headTubeAngle: 73.1,
            bbDrop: 72,
            chainstayLength: 405,
          },
          "58cm": {
            stack: 584,
            reach: 399,
            seatTubeAngle: 73.2,
            headTubeAngle: 73.2,
            bbDrop: 69,
            chainstayLength: 405,
          },
          "60cm": {
            stack: 603,
            reach: 405,
            seatTubeAngle: 72.9,
            headTubeAngle: 73.3,
            bbDrop: 69,
            chainstayLength: 407,
          },
          "63cm": {
            stack: 626,
            reach: 411,
            seatTubeAngle: 72.6,
            headTubeAngle: 73.4,
            bbDrop: 69,
            chainstayLength: 407,
          },
        },
      },
    },
  },

  // ── COLNAGO ──────────────────────────────────────────────
  Colnago: {
    models: {
      "V4Rs (2023-2025)": {
        // ❌ stack/reach not yet re-verified against manufacturer source
        category: "Road",
        modelName: "V4Rs",
        years: "2023-2025",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 31.6,
        sizeType: "numeric_mm",
        sizes: {
          "420 (~50cm)": { stack: 510, reach: 370, seatTubeAngle: 74.5 },
          "455 (~52cm)": { stack: 522, reach: 375, seatTubeAngle: 74.0 },
          "485 (~54cm)": { stack: 539, reach: 383, seatTubeAngle: 73.5 },
          "510 (~56cm)": { stack: 557, reach: 388, seatTubeAngle: 73.5 },
          "530 (~58cm)": { stack: 575, reach: 395, seatTubeAngle: 73.0 },
          "550 (~59cm)": { stack: 593, reach: 403, seatTubeAngle: 73.0 },
          "570 (~61cm)": { stack: 612, reach: 410, seatTubeAngle: 72.5 },
        },
      },
      "C68 Road (2022-2025)": {
        // ❌ stack/reach not yet re-verified against manufacturer source
        category: "Road",
        modelName: "C68 Road",
        years: "2022-2025",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 31.6,
        sizeType: "numeric_mm",
        sizes: {
          "420 (~50cm)": { stack: 510, reach: 370, seatTubeAngle: 74.5 },
          "455 (~52cm)": { stack: 522, reach: 375, seatTubeAngle: 74.0 },
          "485 (~54cm)": { stack: 539, reach: 383, seatTubeAngle: 73.5 },
          "510 (~56cm)": { stack: 557, reach: 388, seatTubeAngle: 73.5 },
          "530 (~58cm)": { stack: 575, reach: 395, seatTubeAngle: 73.0 },
          "550 (~59cm)": { stack: 593, reach: 403, seatTubeAngle: 73.0 },
          "570 (~61cm)": { stack: 612, reach: 410, seatTubeAngle: 72.5 },
        },
      },
    },
  },
  // ── WILIER ───────────────────────────────────────────────
  Wilier: {
    models: {
      "Adlar (2025-2026)": {
        // ✅ stack/reach/STA/HTA/chainstay/wheelbase all verified from wilier.com geometry chart
        category: "Gravel",
        modelName: "Adlar",
        years: "2025-2026",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 74,
        seatpostDiameter: 27.2,
        sizeType: "alpha",
        sizes: {
          XS: {
            stack: 538,
            reach: 395,
            seatTubeAngle: 75,
            headTubeAngle: 70,
            chainstayLength: 423,
            wheelbase: 1030,
          },
          S: {
            stack: 562,
            reach: 404,
            seatTubeAngle: 74.5,
            headTubeAngle: 70,
            chainstayLength: 423,
            wheelbase: 1048,
          },
          M: {
            stack: 586,
            reach: 413,
            seatTubeAngle: 74,
            headTubeAngle: 70,
            chainstayLength: 423,
            wheelbase: 1065,
          },
          L: {
            stack: 610,
            reach: 422,
            seatTubeAngle: 74,
            headTubeAngle: 70,
            chainstayLength: 423,
            wheelbase: 1083,
          },
          XL: {
            stack: 634,
            reach: 431,
            seatTubeAngle: 73.5,
            headTubeAngle: 70,
            chainstayLength: 423,
            wheelbase: 1101,
          },
        },
      },
      "Rave SLR ID2 (2025-2026)": {
        // ✅ stack/reach/STA/HTA/chainstay/wheelbase all verified from wilier.com geometry chart
        category: "Gravel",
        modelName: "Rave SLR ID2",
        years: "2025-2026",
        cockpitType: "semi-integrated",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 27.2,
        sizeType: "alpha",
        sizes: {
          XS: {
            stack: 532,
            reach: 375,
            seatTubeAngle: 75.5,
            headTubeAngle: 69.2,
            chainstayLength: 419,
            wheelbase: 1014,
          },
          S: {
            stack: 546,
            reach: 381,
            seatTubeAngle: 74.7,
            headTubeAngle: 70,
            chainstayLength: 419,
            wheelbase: 1017,
          },
          M: {
            stack: 561,
            reach: 387,
            seatTubeAngle: 74,
            headTubeAngle: 70.8,
            chainstayLength: 421,
            wheelbase: 1022,
          },
          L: {
            stack: 579,
            reach: 393,
            seatTubeAngle: 73.5,
            headTubeAngle: 71.2,
            chainstayLength: 421,
            wheelbase: 1031,
          },
          XL: {
            stack: 597,
            reach: 400,
            seatTubeAngle: 73,
            headTubeAngle: 71.6,
            chainstayLength: 423,
            wheelbase: 1042,
          },
          XXL: {
            stack: 617,
            reach: 408,
            seatTubeAngle: 73,
            headTubeAngle: 72,
            chainstayLength: 423,
            wheelbase: 1052,
          },
        },
      },
      "Filante SLR (2022-2025)": {
        // ✅ stack/reach/STA/HTA/chainstay/wheelbase all verified from wilier.com geometry chart
        category: "Road",
        modelName: "Filante SLR",
        years: "2022-2025",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 27.2,
        sizeType: "alpha",
        sizes: {
          XS: {
            stack: 505,
            reach: 380,
            seatTubeAngle: 75.2,
            headTubeAngle: 70.8,
            chainstayLength: 407,
            wheelbase: 981,
          },
          S: {
            stack: 521,
            reach: 384,
            seatTubeAngle: 74.5,
            headTubeAngle: 72,
            chainstayLength: 407,
            wheelbase: 984,
          },
          M: {
            stack: 538,
            reach: 388,
            seatTubeAngle: 74,
            headTubeAngle: 72.5,
            chainstayLength: 408,
            wheelbase: 990,
          },
          L: {
            stack: 555,
            reach: 391,
            seatTubeAngle: 73.5,
            headTubeAngle: 73,
            chainstayLength: 410,
            wheelbase: 997,
          },
          XL: {
            stack: 571,
            reach: 395,
            seatTubeAngle: 73,
            headTubeAngle: 73,
            chainstayLength: 411,
            wheelbase: 1002,
          },
          XXL: {
            stack: 587,
            reach: 399,
            seatTubeAngle: 72.5,
            headTubeAngle: 73.5,
            chainstayLength: 411,
            wheelbase: 1006,
          },
        },
      },
      "Filante SLR ID2 (2025-2026)": {
        // ✅ stack/reach/STA/HTA/chainstay/wheelbase all verified from wilier.com geometry chart
        category: "Road",
        modelName: "Filante SLR ID2",
        years: "2025-2026",
        cockpitType: "semi-integrated",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 27.2,
        sizeType: "alpha",
        sizes: {
          XS: {
            stack: 505,
            reach: 373.5,
            seatTubeAngle: 75.2,
            headTubeAngle: 70.6,
            chainstayLength: 411,
            wheelbase: 980.5,
          },
          S: {
            stack: 523,
            reach: 380,
            seatTubeAngle: 74.5,
            headTubeAngle: 71.5,
            chainstayLength: 411,
            wheelbase: 985.1,
          },
          M: {
            stack: 541,
            reach: 386.5,
            seatTubeAngle: 74,
            headTubeAngle: 72,
            chainstayLength: 411,
            wheelbase: 992.9,
          },
          L: {
            stack: 559,
            reach: 393,
            seatTubeAngle: 73.5,
            headTubeAngle: 72.5,
            chainstayLength: 411,
            wheelbase: 1000.4,
          },
          XL: {
            stack: 577,
            reach: 400,
            seatTubeAngle: 73,
            headTubeAngle: 73,
            chainstayLength: 413,
            wheelbase: 1010.1,
          },
          XXL: {
            stack: 595,
            reach: 408,
            seatTubeAngle: 73,
            headTubeAngle: 73.5,
            chainstayLength: 413,
            wheelbase: 1018.5,
          },
        },
      },
      "Verticale SLR (2025-2026)": {
        // ✅ stack/reach/STA/HTA/chainstay/wheelbase all verified from wilier.com geometry chart
        category: "Road",
        modelName: "Verticale SLR",
        years: "2025-2026",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 27.2,
        sizeType: "alpha",
        sizes: {
          XS: {
            stack: 505,
            reach: 374,
            seatTubeAngle: 75.2,
            headTubeAngle: 70.6,
            chainstayLength: 405,
            wheelbase: 974,
          },
          S: {
            stack: 523,
            reach: 380,
            seatTubeAngle: 74.5,
            headTubeAngle: 71.5,
            chainstayLength: 407,
            wheelbase: 981,
          },
          M: {
            stack: 541,
            reach: 387,
            seatTubeAngle: 74,
            headTubeAngle: 72,
            chainstayLength: 408,
            wheelbase: 990,
          },
          L: {
            stack: 559,
            reach: 393,
            seatTubeAngle: 73.5,
            headTubeAngle: 72.5,
            chainstayLength: 410,
            wheelbase: 999,
          },
          XL: {
            stack: 577,
            reach: 400,
            seatTubeAngle: 73,
            headTubeAngle: 73,
            chainstayLength: 412,
            wheelbase: 1009,
          },
          XXL: {
            stack: 595,
            reach: 408,
            seatTubeAngle: 73,
            headTubeAngle: 73.5,
            chainstayLength: 412,
            wheelbase: 1017,
          },
        },
      },
    },
  },
  // ── MOOTS ────────────────────────────────────────────────
  Moots: {
    models: {
      "Routt CRD (2023-2025)": {
        // ✅ stack/reach/STA/HTA/bbDrop/chainstay verified from moots.com geometry chart
        // ❌ wheelbase not published by manufacturer
        category: "Gravel",
        modelName: "Routt CRD",
        years: "2023-2025",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 27.2,
        sizeType: "numeric_cm",
        sizes: {
          50: {
            stack: 575,
            reach: 362,
            seatTubeAngle: 75,
            headTubeAngle: 71,
            bbDrop: 71,
            chainstayLength: 430,
          },
          52: {
            stack: 585,
            reach: 362,
            seatTubeAngle: 74.25,
            headTubeAngle: 71,
            bbDrop: 71,
            chainstayLength: 430,
          },
          54: {
            stack: 594,
            reach: 372,
            seatTubeAngle: 74,
            headTubeAngle: 71.5,
            bbDrop: 69,
            chainstayLength: 430,
          },
          55: {
            stack: 603,
            reach: 374,
            seatTubeAngle: 73.5,
            headTubeAngle: 71.5,
            bbDrop: 69,
            chainstayLength: 430,
          },
          56: {
            stack: 615,
            reach: 379,
            seatTubeAngle: 73.5,
            headTubeAngle: 71.5,
            bbDrop: 69,
            chainstayLength: 430,
          },
          58: {
            stack: 624,
            reach: 398,
            seatTubeAngle: 73,
            headTubeAngle: 72,
            bbDrop: 69,
            chainstayLength: 430,
          },
          60: {
            stack: 644,
            reach: 405,
            seatTubeAngle: 72.75,
            headTubeAngle: 72.25,
            bbDrop: 68,
            chainstayLength: 430,
          },
        },
      },
      "Vamoots CRD (2023-2025)": {
        // ✅ stack/reach/STA/HTA/bbDrop/chainstay verified from moots.com geometry chart
        // ❌ wheelbase not published by manufacturer
        category: "Road",
        modelName: "Vamoots CRD",
        years: "2023-2025",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 27.2,
        sizeType: "numeric_cm",
        sizes: {
          48: {
            stack: 535.5,
            reach: 347.7,
            seatTubeAngle: 75,
            headTubeAngle: 71,
            bbDrop: 74,
            chainstayLength: 410,
          },
          50: {
            stack: 546.8,
            reach: 360.1,
            seatTubeAngle: 75,
            headTubeAngle: 71.5,
            bbDrop: 74,
            chainstayLength: 410,
          },
          52: {
            stack: 558.3,
            reach: 372.7,
            seatTubeAngle: 74.5,
            headTubeAngle: 72,
            bbDrop: 72,
            chainstayLength: 410,
          },
          54: {
            stack: 569.6,
            reach: 380.2,
            seatTubeAngle: 74,
            headTubeAngle: 72.5,
            bbDrop: 72,
            chainstayLength: 410,
          },
          55: {
            stack: 580.1,
            reach: 384.9,
            seatTubeAngle: 73.75,
            headTubeAngle: 72.75,
            bbDrop: 72,
            chainstayLength: 410,
          },
          56: {
            stack: 590.5,
            reach: 389.6,
            seatTubeAngle: 73.5,
            headTubeAngle: 73,
            bbDrop: 72,
            chainstayLength: 410,
          },
          58: {
            stack: 599,
            reach: 392.1,
            seatTubeAngle: 73,
            headTubeAngle: 73.25,
            bbDrop: 72,
            chainstayLength: 415,
          },
          60: {
            stack: 619,
            reach: 398.5,
            seatTubeAngle: 72.75,
            headTubeAngle: 73.5,
            bbDrop: 70,
            chainstayLength: 415,
          },
          62: {
            stack: 639.1,
            reach: 407.5,
            seatTubeAngle: 72.75,
            headTubeAngle: 73.75,
            bbDrop: 70,
            chainstayLength: 415,
          },
        },
      },
    },
  },

  // ── BMC ───────────────────────────────────────────────────
  BMC: {
    models: {
      "Teammachine R (2024-2025)": {
        // ✅ stack/reach/STA/HTA/bbDrop/chainstay/wheelbase all verified from bmc-switzerland.com geometry chart
        category: "Road",
        modelName: "Teammachine R",
        years: "2024-2025",
        cockpitType: "integrated",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 27.2,
        sizeType: "numeric_cm",
        sizes: {
          47: {
            stack: 504,
            reach: 368,
            seatTubeAngle: 74,
            headTubeAngle: 71.5,
            bbDrop: 69,
            chainstayLength: 410,
            wheelbase: 968,
          },
          51: {
            stack: 528,
            reach: 378,
            seatTubeAngle: 74,
            headTubeAngle: 71.5,
            bbDrop: 69,
            chainstayLength: 410,
            wheelbase: 986,
          },
          54: {
            stack: 548,
            reach: 387,
            seatTubeAngle: 73.5,
            headTubeAngle: 72.3,
            bbDrop: 69,
            chainstayLength: 410,
            wheelbase: 989,
          },
          56: {
            stack: 563,
            reach: 393,
            seatTubeAngle: 73.5,
            headTubeAngle: 72.3,
            bbDrop: 69,
            chainstayLength: 410,
            wheelbase: 999,
          },
          58: {
            stack: 582,
            reach: 402,
            seatTubeAngle: 73,
            headTubeAngle: 72.3,
            bbDrop: 69,
            chainstayLength: 410,
            wheelbase: 1015,
          },
          61: {
            stack: 606,
            reach: 409,
            seatTubeAngle: 73,
            headTubeAngle: 72.3,
            bbDrop: 69,
            chainstayLength: 410,
            wheelbase: 1030,
          },
        },
      },
    },
  },

  // ── NINER ─────────────────────────────────────────────────
  Niner: {
    models: {
      "BSB 9 RDO (2015-2019)": {
        // ✅ stack/reach/STA/HTA/bbDrop/chainstay/wheelbase all verified from screenshot of manufacturer geometry chart
        category: "CX",
        modelName: "BSB 9 RDO",
        years: "2015-2019",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 27.2,
        sizeType: "numeric_cm",
        sizes: {
          50: {
            stack: 545,
            reach: 369,
            seatTubeAngle: 74,
            headTubeAngle: 71,
            bbDrop: 68,
            chainstayLength: 425,
            wheelbase: 999,
          },
          53: {
            stack: 565,
            reach: 377,
            seatTubeAngle: 73.5,
            headTubeAngle: 71.5,
            bbDrop: 68,
            chainstayLength: 425,
            wheelbase: 1010,
          },
          56: {
            stack: 582,
            reach: 393,
            seatTubeAngle: 73.5,
            headTubeAngle: 72,
            bbDrop: 65,
            chainstayLength: 425,
            wheelbase: 1028,
          },
          59: {
            stack: 603,
            reach: 401,
            seatTubeAngle: 73,
            headTubeAngle: 72.5,
            bbDrop: 65,
            chainstayLength: 425,
            wheelbase: 1037,
          },
          62: {
            stack: 622,
            reach: 409,
            seatTubeAngle: 72.5,
            headTubeAngle: 73,
            bbDrop: 65,
            chainstayLength: 425,
            wheelbase: 1052,
          },
        },
      },
      "RLT 9 (2023-2026)": {
        // ✅ stack/reach/STA/HTA/bbDrop/chainstay/wheelbase all verified from screenshot of manufacturer geometry chart
        category: "Gravel",
        modelName: "RLT 9",
        years: "2023-2026",
        cockpitType: "standard",
        handlebarType: "dropbar",
        wheelSize: "700c",
        suspensionTravel: null,
        seatTubeAngle: 73.5,
        seatpostDiameter: 27.2,
        sizeType: "numeric_cm",
        sizes: {
          47: {
            stack: 537,
            reach: 361,
            seatTubeAngle: 74.5,
            headTubeAngle: 70,
            bbDrop: 75,
            chainstayLength: 435,
            wheelbase: 1005,
          },
          50: {
            stack: 558,
            reach: 365,
            seatTubeAngle: 74,
            headTubeAngle: 70.5,
            bbDrop: 75,
            chainstayLength: 435,
            wheelbase: 1012,
          },
          53: {
            stack: 578,
            reach: 374,
            seatTubeAngle: 73.5,
            headTubeAngle: 71,
            bbDrop: 75,
            chainstayLength: 435,
            wheelbase: 1023,
          },
          56: {
            stack: 594,
            reach: 383,
            seatTubeAngle: 73,
            headTubeAngle: 71.5,
            bbDrop: 70,
            chainstayLength: 435,
            wheelbase: 1036,
          },
          59: {
            stack: 615,
            reach: 397,
            seatTubeAngle: 73,
            headTubeAngle: 72,
            bbDrop: 70,
            chainstayLength: 435,
            wheelbase: 1051,
          },
          62: {
            stack: 636,
            reach: 407,
            seatTubeAngle: 72.5,
            headTubeAngle: 72.5,
            bbDrop: 70,
            chainstayLength: 435,
            wheelbase: 1060,
          },
        },
      },
    },
  },
};

export const BRANDS = Object.keys(BIKES);
